import {
  eachCalendarDayInRange,
  isoToCalendarDayKey,
  normalizeCalendarRangeKeys,
  parseCalendarDayKey,
  shiftMonth,
  toCalendarDayKey,
} from './assetBookingCalendar';
import { bookingRecordAssetId } from './bookingListDisplay';
import { calendarDayIsClosed, isIsoBlackoutDay, resolveCalendarDayOpeningWindow } from './openingHoursBookingValidate';
import { bookingRecordStartMs } from './bookingRecordMutate';

export type OwnerCalendarViewMode = 'day' | 'week' | 'month';

/** Month cell coloring for the owner booking calendar. */
export type OwnerCalendarDayAppearance = 'blackout' | 'closed' | 'openWithBookings' | 'openEmpty';

export function resolveOwnerCalendarDayAppearance(
  asset: Record<string, unknown> | null | undefined,
  dayKey: string,
  bookingCount: number,
): OwnerCalendarDayAppearance {
  if (isIsoBlackoutDay(asset, dayKey)) {
    return 'blackout';
  }
  if (asset && calendarDayIsClosed(asset, dayKey)) {
    return 'closed';
  }
  if (bookingCount > 0) {
    return 'openWithBookings';
  }
  return 'openEmpty';
}

export type BookingHourSpan = {
  startHour: number;
  endHour: number;
  startMinute: number;
  endMinute: number;
};

/** Local hour span for a booking on `dayKey` (for day-view timeline). */
export function bookingHourSpanOnDay(
  booking: Record<string, unknown>,
  dayKey: string,
): BookingHourSpan | null {
  const startIso = booking.start_date;
  const endIso = booking.end_date;
  if (typeof startIso !== 'string' || typeof endIso !== 'string') {
    return null;
  }
  const startKey = isoToCalendarDayKey(startIso);
  const endKey = isoToCalendarDayKey(endIso);
  if (!startKey || !endKey) {
    return null;
  }
  const { start, end } = normalizeCalendarRangeKeys(startKey, endKey);
  if (dayKey < start || dayKey > end) {
    return null;
  }

  const startDt = new Date(startIso.trim());
  const endDt = new Date(endIso.trim());
  if (Number.isNaN(startDt.getTime()) || Number.isNaN(endDt.getTime())) {
    return null;
  }

  if (startKey === endKey && startKey === dayKey) {
    return {
      startHour: startDt.getHours(),
      startMinute: startDt.getMinutes(),
      endHour: endDt.getHours(),
      endMinute: endDt.getMinutes(),
    };
  }

  if (dayKey === start) {
    return {
      startHour: startDt.getHours(),
      startMinute: startDt.getMinutes(),
      endHour: 24,
      endMinute: 0,
    };
  }
  if (dayKey === end) {
    return {
      startHour: 0,
      startMinute: 0,
      endHour: endDt.getHours(),
      endMinute: endDt.getMinutes(),
    };
  }
  return { startHour: 0, startMinute: 0, endHour: 24, endMinute: 0 };
}

/** Inclusive local hour labels shown on the owner day timeline. */
export function ownerDayTimelineHours(
  asset: Record<string, unknown> | null | undefined,
  dayKey: string,
): number[] {
  if (isIsoBlackoutDay(asset, dayKey)) {
    return [];
  }
  if (!asset) {
    return Array.from({ length: 24 }, (_, hour) => hour);
  }
  const window = resolveCalendarDayOpeningWindow(asset, dayKey);
  if (window === 'closed') {
    return [];
  }
  const openHour = window === null ? 0 : Math.floor(window.openMin / 60);
  const closeHour = window === null ? 24 : Math.ceil(window.closeMin / 60);
  const hours: number[] = [];
  for (let hour = openHour; hour < closeHour && hour < 24; hour += 1) {
    hours.push(hour);
  }
  return hours.length > 0 ? hours : Array.from({ length: 24 }, (_, hour) => hour);
}

export function filterBookingsForAsset(
  bookings: Record<string, unknown>[],
  assetId: string,
): Record<string, unknown>[] {
  const want = assetId.trim().toLowerCase();
  if (!want) {
    return [];
  }
  return bookings.filter((booking) => {
    const bid = bookingRecordAssetId(booking);
    return Boolean(bid && bid.toLowerCase() === want);
  });
}

export function bookingOverlapsDayKey(booking: Record<string, unknown>, dayKey: string): boolean {
  const startKey = isoToCalendarDayKey(booking.start_date);
  const endKey = isoToCalendarDayKey(booking.end_date);
  if (!startKey || !endKey) {
    return false;
  }
  const { start, end } = normalizeCalendarRangeKeys(startKey, endKey);
  return dayKey >= start && dayKey <= end;
}

export function bookingsOnDay(
  bookings: Record<string, unknown>[],
  dayKey: string,
): Record<string, unknown>[] {
  return sortBookingsByStart(bookings.filter((booking) => bookingOverlapsDayKey(booking, dayKey)));
}

export function sortBookingsByStart(bookings: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...bookings].sort((a, b) => (bookingRecordStartMs(a) ?? 0) - (bookingRecordStartMs(b) ?? 0));
}

export function shiftCalendarDayKey(dayKey: string, deltaDays: number): string | null {
  const parsed = parseCalendarDayKey(dayKey);
  if (!parsed) {
    return null;
  }
  const date = new Date(parsed.year, parsed.monthIndex, parsed.day);
  date.setDate(date.getDate() + deltaDays);
  return toCalendarDayKey(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Sunday-start week containing `anchorDayKey`. */
export function weekDayKeysForAnchor(anchorDayKey: string): string[] {
  const parsed = parseCalendarDayKey(anchorDayKey);
  if (!parsed) {
    return [];
  }
  const anchor = new Date(parsed.year, parsed.monthIndex, parsed.day);
  const weekStart = new Date(anchor);
  weekStart.setDate(anchor.getDate() - anchor.getDay());
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + index);
    return toCalendarDayKey(day.getFullYear(), day.getMonth(), day.getDate());
  });
}

export function countBookingsPerDay(
  bookings: Record<string, unknown>[],
  dayKeys: readonly string[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const dayKey of dayKeys) {
    counts.set(dayKey, bookingsOnDay(bookings, dayKey).length);
  }
  return counts;
}

export function monthDayKeys(viewYear: number, viewMonthIndex: number): string[] {
  const lastDay = new Date(viewYear, viewMonthIndex + 1, 0).getDate();
  return Array.from({ length: lastDay }, (_, index) =>
    toCalendarDayKey(viewYear, viewMonthIndex, index + 1),
  );
}

export function bookingsInMonth(
  bookings: Record<string, unknown>[],
  viewYear: number,
  viewMonthIndex: number,
): Record<string, unknown>[] {
  const keys = monthDayKeys(viewYear, viewMonthIndex);
  if (keys.length === 0) {
    return [];
  }
  const start = keys[0]!;
  const end = keys[keys.length - 1]!;
  return sortBookingsByStart(
    bookings.filter((booking) => {
      const startKey = isoToCalendarDayKey(booking.start_date);
      const endKey = isoToCalendarDayKey(booking.end_date);
      if (!startKey || !endKey) {
        return false;
      }
      const range = normalizeCalendarRangeKeys(startKey, endKey);
      return range.end >= start && range.start <= end;
    }),
  );
}

export function dayKeysSpanningBooking(booking: Record<string, unknown>): string[] {
  const startKey = isoToCalendarDayKey(booking.start_date);
  const endKey = isoToCalendarDayKey(booking.end_date);
  if (!startKey || !endKey) {
    return [];
  }
  return eachCalendarDayInRange(startKey, endKey);
}

export { shiftMonth };
